import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { usePaymentSession } from '../../hooks/usePaymentSession';
import PaymentMethodsForm from '../../components/PaymentForm/PaymentMethodsForm';
import { formatCurrency } from '../../utils/strings';
import {
  buildPaymentsPayload,
  isAlreadyPaidMessage,
  PaymentMethodsFormSubmitData,
} from '../../services/pagarme';
import { PaymentEntryPayload } from '../../shared/interfaces/payment.interface';
import { getFormPaymentMethod } from '../../utils/getFormPaymentMethod';
import { getInvoicePaymentBlockReason } from '../../utils/getInvoicePaymentBlockReason';
import handleResponseError from '../../utils/handleResponseError';

const PRECONDITION_FAILED = 412;

const PaymentPage: React.FC = () => {
  const { sessionToken } = useParams<{ sessionToken: string }>();
  const { sessionData, loading, error, isProcessing, processPayment } = usePaymentSession(
    sessionToken || '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [boletoUrl, setBoletoUrl] = useState<string | null>(null);

  const handleFormSubmit = async (data: PaymentMethodsFormSubmitData) => {
    setSubmitting(true);

    let payments: PaymentEntryPayload[];

    try {
      payments = await buildPaymentsPayload(data);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao preparar dados do pagamento');
      setSubmitting(false);
      return;
    }

    try {
      const result = await processPayment(payments);
      toast.success('Pagamento realizado com sucesso!');
      setPaymentCompleted(true);
      const resultBoletoUrl = result?.mundipaggBoletoUrl || null;
      setBoletoUrl(resultBoletoUrl);
      if (resultBoletoUrl) {
        window.open(resultBoletoUrl, '_blank', 'noreferrer');
      } else {
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (err: any) {
      const statusCode = err?.statusCode;
      const message = typeof err?.message === 'string' ? err.message : '';

      if (statusCode === PRECONDITION_FAILED && isAlreadyPaidMessage(message)) {
        setAlreadyPaid(true);
      } else if (statusCode === PRECONDITION_FAILED) {
        toast.error(message || 'Cartão recusado. Tente novamente com outro cartão.');
      } else {
        handleResponseError(err, 'Erro ao processar pagamento');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!sessionToken) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          Token de sessão inválido. Verifique o link compartilhado.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Carregando dados do pagamento...</p>
      </Container>
    );
  }

  if (error || !sessionData) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          {error || 'Ocorreu um erro ao carregar os dados de pagamento.'}
        </Alert>
      </Container>
    );
  }

  const allowedMethods = getFormPaymentMethod(sessionData.paymentMethod);
  const initialBlockReason = getInvoicePaymentBlockReason(sessionData.status);

  const renderPaymentSection = () => {
    if (alreadyPaid) {
      return (
        <Alert variant="info">
          <strong>Este pedido já foi pago.</strong>
          <p className="mb-0 mt-2">
            Não é necessário realizar o pagamento novamente.
          </p>
        </Alert>
      );
    }

    if (paymentCompleted) {
      return (
        <Alert variant="success">
          <strong>Pagamento realizado com sucesso!</strong>
          {boletoUrl ? (
            <p className="mb-0 mt-2">
              <a href={boletoUrl} target="_blank" rel="noreferrer">
                Ver / baixar boleto
              </a>
            </p>
          ) : (
            <p className="mb-0 mt-2">Atualizando a página...</p>
          )}
        </Alert>
      );
    }

    if (initialBlockReason) {
      return (
        <Alert variant="warning">
          <strong>Pagamento indisponível.</strong>
          <p className="mb-0 mt-2">{initialBlockReason}</p>
        </Alert>
      );
    }

    if (sessionData.expired) {
      return (
        <Alert variant="warning">
          <strong>Prazo de pagamento expirado.</strong>
          <p className="mb-0 mt-2">
            O prazo para pagar este pedido já passou. Entre em contato com
            nosso time comercial.
          </p>
        </Alert>
      );
    }

    if (sessionData.boletoUrl) {
      return (
        <Alert variant="info">
          <strong>O boleto deste pedido já foi emitido.</strong>
          <p className="mb-0 mt-2">
            <a href={sessionData.boletoUrl} target="_blank" rel="noreferrer">
              Ver / baixar boleto
            </a>
          </p>
        </Alert>
      );
    }

    if (allowedMethods.length === 0) {
      return (
        <Alert variant="danger">
          Este pedido não pode ser pago por este link. Entre em contato
          com nosso time comercial.
        </Alert>
      );
    }

    return (
      <>
        <PaymentMethodsForm
          totalAmount={Math.round((sessionData.totalInvoice || 0) * 100)}
          maxInstallments={sessionData.cardMaxInstallmentNumber || 1}
          allowedMethods={allowedMethods}
          onSubmit={handleFormSubmit}
        />
        {(submitting || isProcessing) && (
          <div className="text-center mt-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Processando pagamento...
          </div>
        )}
      </>
    );
  };

  return (
    <Container className="mt-5 mb-5">
      <Row>
        <Col lg={8} md={12}>
          <h1>Pagamento</h1>
          <p className="text-muted">Cliente: {sessionData.customerName}</p>

          {renderPaymentSection()}
        </Col>
        <Col lg={4} md={12}>
          <div className="border rounded p-4 bg-light">
            <h5>Resumo do Pagamento</h5>
            <hr />
            <p>
              <strong>{sessionData.invoiceNumber}</strong>
            </p>
            <p>
              <strong>Valor Total:</strong> {formatCurrency(sessionData.totalInvoice)}
            </p>
            {sessionData.cardMaxInstallmentNumber && (
              <p>
                <strong>Máximo de Parcelas:</strong> {sessionData.cardMaxInstallmentNumber}x
              </p>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PaymentPage;
